import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Plus, Minus } from "lucide-react";
import { useState } from "react";
import { MaskText, FadeUp, EASE } from "../components/Reveal";
import {
  IMAGES, SCENT_STATS, SCENT_REASONS, SCENT_PROCESS,
  SCENT_FACTS, SCENT_FAQS, INDUSTRY_CARDS, COMPLIANCE,
} from "../data/site";

const Hero = () => (
  <section className="relative min-h-[88vh] overflow-hidden bg-cream" data-testid="cs-hero">
    <motion.img
      src={IMAGES.commercialHero}
      alt="A commercial scent diffuser installed beside a hotel corridor"
      className="absolute inset-0 w-full h-full object-cover object-center"
      initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.5, ease: EASE }}
    />
    <div className="absolute inset-0 hidden md:block pointer-events-none"
      style={{ background: "linear-gradient(to right, rgba(244,238,226,0.97) 0%, rgba(244,238,226,0.9) 30%, rgba(244,238,226,0.4) 52%, rgba(244,238,226,0) 72%)" }} />
    <div className="absolute inset-0 md:hidden pointer-events-none"
      style={{ background: "linear-gradient(to top, rgba(244,238,226,0.98) 0%, rgba(244,238,226,0.85) 38%, rgba(244,238,226,0.1) 70%)" }} />
    <div className="relative z-10 min-h-[88vh] max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end md:justify-center pb-16 md:pb-0">
      <div className="max-w-xl">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
          className="eyebrow mb-7">Commercial Scenting</motion.p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-[4.75rem] leading-[0.95] tracking-tight text-ink">
          <MaskText lines={["Give your business", "a signature scent."]} delay={0.3} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
          className="text-ink-muted text-lg leading-relaxed mt-8 max-w-lg">
          The best venues are recognised with your eyes shut. We design, install and look after
          scenting for hotels, gyms, salons, showrooms and stores across Australia.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
          className="flex flex-wrap gap-4 mt-11">
          <Link to="/enquiries" data-testid="cs-hero-cta"
            className="group inline-flex items-center gap-3 px-9 py-4 bg-[#221d17] text-cream text-xs font-semibold uppercase tracking-[0.2em] hover:bg-black transition-colors duration-300">
            Request a consultation
            <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
          <Link to="/shop" className="inline-flex items-center gap-3 px-9 py-4 border border-[#221d17] text-[#221d17] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#221d17] hover:text-cream transition-colors duration-300">
            Shop diffusers
          </Link>
        </motion.div>
      </div>
    </div>
  </section>
);

const Stats = () => (
  <section className="bg-[#221d17]" data-testid="cs-stats">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-24">
      <FadeUp className="max-w-xl">
        <p className="eyebrow mb-5">The science of scent</p>
        <h2 className="font-serif text-3xl md:text-[2.6rem] leading-tight text-[#f4eee2]">
          Smell is the sense tied hardest to memory.
        </h2>
      </FadeUp>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#3a3128] mt-14 border border-[#3a3128]">
        {SCENT_STATS.map((s, i) => (
          <FadeUp key={s.value} delay={i * 0.1} className="bg-[#221d17] p-9" data-testid={`cs-stat-${i}`}>
            <p className="font-serif text-5xl text-[#d8c9a3]">{s.value}</p>
            <p className="text-sm text-[#c9bda9] leading-relaxed mt-4">{s.label}</p>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Reasons = () => (
  <section className="bg-cream" data-testid="cs-reasons">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-28">
      <FadeUp className="max-w-2xl mb-14">
        <h2 className="font-serif text-3xl md:text-[2.6rem] tracking-tight text-ink leading-tight">
          Should your business be using scent?
        </h2>
        <span className="block w-14 h-px bg-gold mt-5" />
      </FadeUp>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-cream-border border border-cream-border">
        {SCENT_REASONS.map((r, i) => (
          <FadeUp key={r.title} delay={(i % 3) * 0.08} className="bg-[#fbf8f1] p-9" data-testid={`cs-reason-${i}`}>
            <span className="font-serif text-sm text-gold">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="font-serif text-xl text-ink leading-snug mt-4">{r.title}</h3>
            <p className="text-sm text-ink-muted leading-relaxed mt-4">{r.body}</p>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Process = () => (
  <section className="bg-cream-alt border-y border-cream-border" data-testid="cs-process">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <div>
          <FadeUp>
            <p className="eyebrow mb-5">How it works</p>
            <h2 className="font-serif text-3xl md:text-[2.6rem] tracking-tight text-ink leading-tight">
              We come to you, install it, and keep it running.
            </h2>
            <span className="block w-14 h-px bg-gold mt-5" />
            <p className="text-ink-muted leading-relaxed mt-7 max-w-lg">
              You don't need to learn anything about diffusion. We handle the sizing, the fitting
              and the refills — you just tell us how you want the place to feel.
            </p>
          </FadeUp>
          <div className="mt-12 border-t border-cream-border">
            {SCENT_PROCESS.map((s, i) => (
              <FadeUp key={s.no} delay={i * 0.07} className="flex gap-6 py-7 border-b border-cream-border" data-testid={`cs-step-${i}`}>
                <span className="font-serif text-xl text-gold shrink-0 w-9">{s.no}</span>
                <div>
                  <h3 className="font-serif text-xl text-ink leading-snug">{s.title}</h3>
                  <p className="text-sm text-ink-muted leading-relaxed mt-2">{s.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
        <FadeUp delay={0.15} className="lg:sticky lg:top-28">
          <img src={IMAGES.installService} alt="Our technician installing a wall-mounted diffuser"
            className="w-full aspect-[4/5] object-cover border border-cream-border" />
          <div className="grid grid-cols-2 gap-px bg-cream-border border-x border-b border-cream-border">
            {SCENT_FACTS.map((f) => (
              <div key={f.value} className="bg-[#fbf8f1] p-6" data-testid={`cs-fact-${f.value}`}>
                <p className="font-serif text-2xl text-ink">{f.value}</p>
                <p className="text-xs text-ink-muted mt-1 leading-relaxed">{f.label}</p>
              </div>
            ))}
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

const Industries = () => (
  <section className="bg-cream" data-testid="cs-industries">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-28">
      <FadeUp className="max-w-2xl mb-12">
        <h2 className="font-serif text-3xl md:text-[2.6rem] tracking-tight text-ink leading-tight">
          Scenting by industry
        </h2>
        <span className="block w-14 h-px bg-gold mt-5" />
      </FadeUp>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {INDUSTRY_CARDS.map((ind, i) => (
          <FadeUp key={ind.label} delay={(i % 6) * 0.06}>
            <div className="group relative aspect-[3/4] overflow-hidden" data-testid={`cs-industry-${i}`}>
              <img src={ind.img} alt={ind.label}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#161009]/85 via-[#161009]/20 to-transparent" />
              <span className="absolute bottom-4 left-4 right-4 text-[#f4eee2] text-sm font-serif leading-tight">{ind.label}</span>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Compliance = () => (
  <section className="bg-cream-alt border-y border-cream-border" data-testid="cs-compliance">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-14">
      <FadeUp>
        <h2 className="font-serif text-3xl md:text-[2.4rem] tracking-tight text-ink leading-tight">
          The paperwork your workplace will ask for
        </h2>
        <span className="block w-14 h-px bg-gold mt-5" />
        <p className="text-ink-muted leading-relaxed mt-7">
          Commercial sites need documentation, so we supply it as standard. Safety Data Sheets for
          every fragrance, IFRA compliance, and non-toxic formulations suitable for occupied spaces.
        </p>
      </FadeUp>
      <FadeUp delay={0.1}>
        <ul className="border-t border-cream-border">
          {COMPLIANCE.map((c) => (
            <li key={c} className="flex items-start gap-4 py-4 border-b border-cream-border">
              <Check size={16} className="text-gold shrink-0 mt-1" />
              <span className="text-sm text-ink leading-relaxed">{c}</span>
            </li>
          ))}
        </ul>
      </FadeUp>
    </div>
  </section>
);

const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="bg-cream" data-testid="cs-faq">
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-28">
        <FadeUp className="mb-12">
          <h2 className="font-serif text-3xl md:text-[2.6rem] tracking-tight text-ink leading-tight">
            Questions we get asked
          </h2>
          <span className="block w-14 h-px bg-gold mt-5" />
        </FadeUp>
        <div className="border-t border-cream-border">
          {SCENT_FAQS.map((f, i) => (
            <div key={f.q} className="border-b border-cream-border" data-testid={`cs-faq-${i}`}>
              <button onClick={() => setOpen(open === i ? -1 : i)}
                aria-expanded={open === i}
                className="w-full flex items-start justify-between gap-6 text-left py-6 group"
                data-testid={`cs-faq-toggle-${i}`}>
                <span className="font-serif text-lg md:text-xl text-ink leading-snug group-hover:text-gold transition-colors">{f.q}</span>
                {open === i ? <Minus size={17} className="text-gold shrink-0 mt-1.5" /> : <Plus size={17} className="text-ink-muted shrink-0 mt-1.5" />}
              </button>
              {open === i && <p className="text-sm text-ink-muted leading-relaxed pb-7 max-w-2xl">{f.a}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTA = () => (
  <section className="bg-[#221d17]" data-testid="cs-cta">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-28 text-center">
      <FadeUp>
        <h2 className="font-serif text-4xl md:text-5xl text-[#f4eee2] leading-tight max-w-2xl mx-auto">
          Ready when your space is.
        </h2>
        <p className="text-[#c9bda9] leading-relaxed mt-6 max-w-xl mx-auto">
          Tell us about the space and we'll come back with the right system, coverage and a
          shortlist of fragrances. Usually within one business day.
        </p>
      </FadeUp>
      <FadeUp delay={0.12}>
        <Link to="/enquiries" data-testid="cs-cta-button"
          className="group inline-flex items-center gap-3 mt-10 px-10 py-5 bg-[#d8c9a3] text-[#221d17] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#e6dabb] transition-colors duration-300">
          Request a consultation
          <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
        </Link>
      </FadeUp>
    </div>
  </section>
);

export default function CommercialScenting() {
  return (
    <div data-testid="commercial-scenting-page">
      <Hero />
      <Stats />
      <Reasons />
      <Process />
      <Industries />
      <Compliance />
      <Faq />
      <CTA />
    </div>
  );
}
